import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { useLive } from "../lib/use-live";
import { useT } from "../context/locale";
import type { TranslationKey } from "../i18n";
import { useToast } from "../context/toast";
import { money } from "../lib/format";
import { useDateTime, useRelative } from "../lib/use-relative";
import {
  ActionButton,
  ActionIcon,
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  LoadingRows,
  Modal,
  Panel,
  RowActions,
  Table,
  Td,
  Tr,
  UserStatusBadge,
} from "../components/ui";
import {
  FilterBar,
  PageBar,
  SortHeader,
  useFilters,
  useSort,
} from "../components/ui/filters";
import {
  CreatePlayerDialog,
  DeletePlayerDialog,
  EditPlayerDialog,
} from "../components/player-dialogs";
import { CreditDialog } from "../components/credit-dialogs";
import type { AdminUser, HistoryProfile, HistoryResult, Paged } from "../types/api";
import {
  HistoryProfileForm,
  defaultProfile,
  isoDay,
  profileIsValid,
} from "../components/history-profile-form";

/**
 * Test players.
 *
 * Replaces the old test-data generator. The difference is not cosmetic: these
 * are ordinary player accounts with ordinary bets and ledger rows, so they show
 * up in reports and aggregates like anyone else. Nothing here creates a second
 * class of data.
 */
export function TestPlayers() {
  const t = useT();
  const relative = useRelative();
  const dateTime = useDateTime();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const filters = useFilters({ q: "", status: "" }, () => setPage(1));
  const sort = useSort("createdAt", "desc", () => setPage(1));

  const players = useLive<Paged<AdminUser>>("players", {
        page,
        perPage,
        sort: sort.sort,
        order: sort.order,
        q: filters.applied.q || undefined,
        status: filters.applied.status || undefined,
      });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState<AdminUser | null>(null);
  const [historyFor, setHistoryFor] = useState<AdminUser | null>(null);
  const [creditFor, setCreditFor] = useState<AdminUser | null>(null);

  return (
    <div className="space-y-5">
      <div
        className="panel px-5 py-4"
        style={{ borderLeft: "3px solid var(--accent)" }}
      >
        <p className="text-[13px] font-semibold">
          {t("testPlayers.subtitle")}
        </p>
        <p className="text-muted mt-1.5 text-[12px]">
{t("testPlayers.realExplain")}
        </p>
      </div>

      <FilterBar
        loading={players.loading}
        state={filters}
        filters={[
          { key: "q", label: t("common.username"), placeholder: t("players.filter.usernamePlaceholder") },
          {
            key: "status",
            label: t("common.status"),
            type: "select",
            options: [
              { value: "", label: t("common.anyStatus") },
              ...(["pending_review", "active", "banned", "rejected"] as const).map(
                (v) => ({ value: v, label: t(`statusOption.${v}` as TranslationKey) }),
              ),
            ],
          },
        ]}
      />

      <Panel
        eyebrow={t("nav.group.people")}
        title={t("testPlayers.title")}
        bodyClassName=""
        actions={
          <>
            <Button size="sm" onClick={players.reload}>
              {t("action.refresh")}
            </Button>
            <Button size="sm" variant="gold" onClick={() => setCreating(true)}>
              {t("players.new")}
            </Button>
          </>
        }
      >
        {players.error ? (
          <div className="p-5">
            <ErrorNote message={players.error} onRetry={players.reload} />
          </div>
        ) : (
          <>
            <Table
              minWidth="58rem"
              head={[
                <SortHeader key="u" field="username" state={sort}>
                  {t("common.player")}
                </SortHeader>,
                <SortHeader key="s" field="status" state={sort}>
                  {t("common.status")}
                </SortHeader>,
                <SortHeader
                  key="b"
                  field="balanceCents"
                  state={sort}
                  align="right"
                >
                  {t("common.balance")}
                </SortHeader>,
                <SortHeader key="c" field="createdAt" state={sort}>
                  {t("common.joined")}
                </SortHeader>,
                t("testPlayers.history"),
                t("common.actions"),
              ]}
            >
              {players.loading ? (
                <LoadingRows columns={6} />
              ) : players.data && players.data.items.length === 0 ? (
                <Tr>
                  <Td colSpan={6}>
                    <EmptyState
                      title={t("testPlayers.empty")}
                      detail={t("testPlayers.emptyDetail")}
                      action={
                        <Button
                          variant="gold"
                          onClick={() => setCreating(true)}
                        >
                          {t("players.new")}
                        </Button>
                      }
                    />
                  </Td>
                </Tr>
              ) : (
                (players.data?.items ?? []).map((user) => (
                  <Tr key={user.id}>
                    <Td>
                      <Link
                        to={`/players/${user.id}`}
                        className="font-semibold hover:underline"
                      >
                        {user.username}
                      </Link>
                      <span className="text-dim ml-2 text-[12px]">
                        {user.fullName}
                      </span>
                    </Td>
                    <Td>
                      <UserStatusBadge status={user.status} />
                    </Td>
                    <Td className="money accent text-right">
                      {money(user.balanceCents)}
                    </Td>
                    <Td className="text-dim text-[12px]">
                      {relative(user.createdAt)}
                    </Td>
                    {/* Whether this account has a recipe, and whether it has
                        been run — the two things that decide what the History
                        button will do when it is pressed. */}
                    <Td className="text-dim text-[12px]">
                      {!user.historyProfile ? (
                        "—"
                      ) : user.historyProfile.lastGeneratedAt ? (
                        <span title={dateTime(user.historyProfile.lastGeneratedAt)}>
                          {relative(user.historyProfile.lastGeneratedAt)}
                        </span>
                      ) : (
                        <Badge tone="neutral">{t("history.neverRun")}</Badge>
                      )}
                    </Td>
                    <Td>
                      <RowActions>
                        {/* Test players are ordinary players, so they get the
                            same credit operations as everyone else. */}
                        <ActionButton
                          icon={ActionIcon.credit}
                          tone="primary"
                          onClick={() => setCreditFor(user)}
                        >
                          {t("players.credit")}
                        </ActionButton>
                        <ActionButton
                          icon={ActionIcon.history}
                          onClick={() => setHistoryFor(user)}
                        >
                          {t("testPlayers.history")}
                        </ActionButton>
                        <ActionButton
                          icon={ActionIcon.edit}
                          onClick={() => setEditing(user)}
                        >
                          {t("action.edit")}
                        </ActionButton>
                        <ActionButton
                          icon={ActionIcon.delete}
                          tone="danger"
                          onClick={() => setDeleting(user)}
                        >
                          {t("action.delete")}
                        </ActionButton>
                      </RowActions>
                    </Td>
                  </Tr>
                ))
              )}
            </Table>

            {players.data ? (
              <PageBar
                page={players.data.page}
                pages={players.data.pages}
                total={players.data.total}
                perPage={perPage}
                onPage={setPage}
                onPerPage={(size) => {
                  setPerPage(size);
                  setPage(1);
                }}
              />
            ) : null}
          </>
        )}
      </Panel>

      <CreatePlayerDialog
        open={creating}
        onClose={() => setCreating(false)}
        onDone={players.reload}
        title={t("testPlayers.new")}
        note={t("testPlayers.createNote")}
      />
      <EditPlayerDialog
        user={editing}
        onClose={() => setEditing(null)}
        onDone={players.reload}
      />
      <DeletePlayerDialog
        user={deleting}
        onClose={() => setDeleting(null)}
        onDone={players.reload}
      />

      <CreditDialog
        user={creditFor}
        onClose={() => setCreditFor(null)}
        onDone={players.reload}
      />
      <HistoryDialog
        user={historyFor}
        onClose={() => setHistoryFor(null)}
        onDone={players.reload}
      />
    </div>
  );
}

/**
 * A player's history profile: read it, change it, run it, or throw it away.
 *
 * The same form the create dialog uses, so an operator meets one shape of the
 * question whichever door they came through. What differs is what can be done
 * with it once an account exists — saving without running, regenerating to
 * replace what an earlier run wrote, and clearing it out.
 *
 * "Save" and "Save and regenerate" are deliberately separate. Editing a profile
 * is not the same decision as rewriting a year of somebody's history, and a
 * single button would make every typo correction do both.
 */
function HistoryDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const dateTime = useDateTime();

  const [profile, setProfile] = useState<HistoryProfile>(defaultProfile);
  const [preview, setPreview] = useState<HistoryResult | null>(null);
  const [busy, setBusy] = useState<null | "save" | "run" | "preview" | "clear">(null);
  const [error, setError] = useState<string | null>(null);

  /*
   * Seeded from the account, and only when the dialog is handed one.
   *
   * The players list is a live subscription answered on a ten-second heartbeat;
   * re-seeding on every answer would wipe a half-made edit out from under
   * whoever was typing it. `useLive` keeps the object's identity while the data
   * is unchanged, so this fires on open and not on the beat.
   */
  useEffect(() => {
    if (!user) return;
    setProfile(user.historyProfile ? toFormProfile(user.historyProfile) : defaultProfile());
    setPreview(null);
    setError(null);
  }, [user]);

  const valid = profileIsValid(profile);
  const hasProfile = Boolean(user?.historyProfile);
  const lastRun = user?.historyProfile?.lastGeneratedAt ?? null;

  async function run(
    what: "save" | "run" | "preview" | "clear",
    action: () => Promise<void>,
  ) {
    setBusy(what);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("error.buildHistory"));
    } finally {
      setBusy(null);
    }
  }

  const save = () =>
    run("save", async () => {
      if (!user) return;
      await api.updateUser(user.id, { historyProfile: profile });
      toast.success(t("toast.profileSaved"));
      onDone();
    });

  const showPreview = () =>
    run("preview", async () => {
      if (!user) return;
      setPreview(await api.generateHistory(user.id, { profile, previewOnly: true }));
    });

  const saveAndRun = () =>
    run("run", async () => {
      if (!user) return;
      await api.updateUser(user.id, { historyProfile: profile });
      const result = await api.generateHistory(user.id, { replace: true });
      toast.success(
        t("toast.historyBuilt"),
        [
          t("toast.historyBuiltDetail", {
            bets: result.betsCreated,
            won: result.betsWon,
            deposits: result.depositCount,
            withdrawals: result.withdrawalCount,
            balance: money(result.balanceCents),
          }),
          result.cleared?.bets
            ? t("toast.historyReplaced", {
                bets: result.cleared.bets,
                rows: result.cleared.transactions,
              })
            : null,
          ...result.warnings,
        ]
          .filter(Boolean)
          .join(" "),
      );
      onDone();
      onClose();
    });

  const clear = () =>
    run("clear", async () => {
      if (!user || !window.confirm(t("history.clearConfirm"))) return;
      const { cleared } = await api.clearHistory(user.id);
      toast.success(
        t("toast.historyCleared"),
        t("toast.historyClearedDetail", {
          bets: cleared.bets,
          rows: cleared.transactions,
          rounds: cleared.rounds,
        }),
      );
      onDone();
      onClose();
    });

  return (
    <Modal
      open={Boolean(user)}
      onClose={onClose}
      eyebrow={t("nav.group.people")}
      title={t("history.title", { name: user?.username ?? "" })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button onClick={save} loading={busy === "save"} disabled={!valid || Boolean(busy)}>
            {t("history.save")}
          </Button>
          <Button
            variant="gold"
            onClick={saveAndRun}
            loading={busy === "run"}
            disabled={!valid || Boolean(busy)}
          >
            {hasProfile && lastRun ? t("history.saveAndRun") : t("history.run")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-muted text-[13px]">{t("history.explain")}</p>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-dim text-[12px]">
            {!hasProfile
              ? t("history.noProfile")
              : lastRun
                ? t("history.lastRun", { when: dateTime(lastRun) })
                : t("history.neverRun")}
          </span>
          <span className="flex-1" />
          <Button size="sm" onClick={showPreview} loading={busy === "preview"} disabled={!valid}>
            {t("history.preview")}
          </Button>
          {lastRun ? (
            <Button
              size="sm"
              variant="danger"
              onClick={clear}
              loading={busy === "clear"}
              disabled={Boolean(busy)}
            >
              {t("history.clear")}
            </Button>
          ) : null}
        </div>

        {/* The plan, before anything is written. A year of history is not
            something to discover the size of after the fact. */}
        {preview ? (
          <div
            className="rounded-lg px-4 py-3"
            style={{ background: "var(--surface-inset)" }}
          >
            <p className="eyebrow mb-1.5">{t("history.previewTitle")}</p>
            <ul className="text-muted space-y-1 text-[12px]">
              <li>
                ·{" "}
                {t("history.previewBets", {
                  bets: preview.betsPlanned,
                  days: preview.playingDays,
                  wins: preview.winsPlanned,
                })}
              </li>
              <li>· {t("history.previewDeposits", { count: preview.depositCount })}</li>
              <li>· {t("history.previewWithdrawals", { count: preview.withdrawalCount })}</li>
              <li>
                ·{" "}
                {t("history.previewWindow", {
                  start: preview.window.start,
                  end: preview.window.end,
                  zone: preview.timeZone,
                })}
              </li>
            </ul>
            <SampleDays sample={preview.sample} />
            {preview.warnings.length ? (
              <ul className="mt-2 space-y-1 text-[11px]" style={{ color: "var(--warning)" }}>
                {preview.warnings.map((warning) => (
                  <li key={warning}>· {warning}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <HistoryProfileForm value={profile} onChange={setProfile} disabled={Boolean(busy)} />

        {lastRun ? (
          <p className="text-dim text-[12px]">{t("history.replaceNote")}</p>
        ) : null}
        {busy === "run" ? <p className="text-dim text-[12px]">{t("history.working")}</p> : null}
        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

/**
 * The first few days of a preview, record by record.
 *
 * The counts above say *how much* will be written; this says what a day will
 * actually read like in the player's history — the order and the times — which
 * is the thing operators check, and the thing that was wrong when they
 * complained. Times are in the profile's zone, as the server laid them out.
 */
function SampleDays({ sample }: { sample: HistoryResult["sample"] }) {
  const t = useT();
  if (!sample?.length) return null;

  const label = (event: HistoryResult["sample"][number]["events"][number]) => {
    switch (event.kind) {
      case "deposit":
        return t("history.step.deposit");
      case "withdrawal":
        return t("history.step.withdraw");
      case "payout":
        return t("history.sample.win");
      default:
        return event.won ? t("history.sample.betWon") : t("history.sample.betLost");
    }
  };
  const tone = (kind: string) =>
    kind === "payout" ? "var(--success)" : kind === "bet" ? "var(--text)" : "var(--accent)";

  return (
    <div className="mt-3 space-y-2">
      <p className="eyebrow">{t("history.sampleTitle")}</p>
      <div className="grid gap-2">
        {sample.map((day) => (
          <div key={day.date} className="rounded-md px-3 py-2" style={{ background: "var(--surface)" }}>
            <p className="text-dim tnum mb-1 text-[11px] font-semibold">{day.date}</p>
            <ul className="space-y-0.5 text-[11px]">
              {day.events.map((event, index) => (
                <li key={index} className="flex items-center gap-2">
                  <span className="text-dim tnum w-14 shrink-0">{event.time}</span>
                  <span className="flex-1 font-semibold whitespace-nowrap" style={{ color: tone(event.kind) }}>
                    {label(event)}
                  </span>
                  <span className="tnum text-muted">{money(event.amountCents)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The stored profile as the form wants it.
 *
 * The API sends timestamps; the date inputs hold `YYYY-MM-DD` in the operator's
 * own zone. Converting here rather than in the form keeps the form's value the
 * one shape it renders, and means a profile that came back from the server and
 * one built from scratch are indistinguishable to it.
 */
function toFormProfile(stored: HistoryProfile): HistoryProfile {
  const base = defaultProfile();
  return {
    ...base,
    ...stored,
    from: isoDay(new Date(stored.from)),
    to: isoDay(new Date(stored.to)),
    // Profiles saved before the gap could wander hold a single `everyNDays`,
    // which meant exactly that many days — so both ends take it.
    intervalDays:
      stored.intervalDays ??
      (stored.everyNDays
        ? { min: stored.everyNDays, max: stored.everyNDays }
        : base.intervalDays),
    /*
     * Profiles saved before windows and zones: whole `hours` on the server's
     * clock become a window from the first hour to the end of the last. The zone
     * is left as this browser's — saving re-pins the profile to what the
     * operator sees, which is the fix for those hours meaning the server's.
     */
    timeZone: stored.timeZone ?? base.timeZone,
    window:
      stored.window ??
      (stored.hours
        ? {
            start: `${String(stored.hours.min).padStart(2, "0")}:00`,
            end: `${String((stored.hours.max + 1) % 24).padStart(2, "0")}:00`,
          }
        : base.window),
    hours: undefined,
    /*
     * Only the fields that still mean something. Profiles saved before every
     * day became Deposit → bets → Withdraw carry how many deposits and
     * withdrawals and how often, an opening deposit and a random order; the
     * generator ignores those, so the form drops them rather than send them back.
     */
    sequence: {
      settle: stored.sequence?.settle ?? base.sequence.settle,
      gapMinutes: stored.sequence?.gapMinutes ?? base.sequence.gapMinutes,
    },
    deposits: {
      amountCents: stored.deposits?.amountCents ?? base.deposits.amountCents,
      autoTopUp: stored.deposits?.autoTopUp ?? base.deposits.autoTopUp,
    },
    withdrawals: {
      percentOfBalance: stored.withdrawals?.percentOfBalance ?? base.withdrawals.percentOfBalance,
      minCents: stored.withdrawals?.minCents ?? base.withdrawals.minCents,
    },
  };
}
